import { type RecordClickIntent } from '../../../ai/actions/record-actions.appearance';
import {
  type BulkAction,
  type BulkActionsDisplayMode,
  type RowAction,
  type RowActionsDisplayMode,
} from '../../../ai/models/actions';
import {
  type AiWorkflowFinalizationEntry,
  type BaseAiElementDefinition,
  type ContainerFieldFilter,
  type ContainerFieldMapping,
  type ElementContext,
  type WorkflowContext,
} from '../../../ai/models/ai.model';
import { type EmptyImageState, type ImageBehavior } from '../../../ai/models/image';
import {
  type FillStyle,
  type FontWeightStyle,
  type HorizontalAlignmentStyle,
  type OutlineStyle,
  type PaddingStyle,
  type ShadowStyle,
  type WidthStyle,
} from '../../../ai/models/styles';

import { type ListFieldSlot } from './list.slots';

export interface ListDefinition extends BaseAiElementDefinition<'list'> {
  template?: ListTemplate;
  /** scrolling/pagination mode. Ignored for notification and subscription (always a full list);
   * only the two scrolling variants are honoured for the html template. */
  behavior?: ListBehavior;
  /** derived from {@link behavior} when absent; ignored for notification. */
  mobileBehavior?: ListMobileBehavior;
  rows?: number;
  columns?: number;
  showTitle?: boolean;
  title?: string;
  iconName?: string;
  emptyMessage?: string;
  /** Hides the whole element when its data source returns no records. */
  hideWhenEmpty?: boolean;
  styles?: ListStyles;
  width?: WidthStyle;
  search?: boolean;
  itemActions?: RowAction[];
  itemActionsDisplayMode?: RowActionsDisplayMode;
  itemClick?: RecordClickIntent;
  bulkActions?: BulkAction[];
  bulkActionsDisplayMode?: BulkActionsDisplayMode;
  addNewItem?: AddNewItemButton;
  iconIndicator?: IconIndicator;
  sortBy?: SortByEntry[];
  fieldMappings?: ContainerFieldMapping<ListFieldSlot>[];
  fieldFilters?: ContainerFieldFilter[];
  customTemplateCode?: string;
}

export interface SortByEntry {
  field: string;
  direction?: ListSortDirection;
}

export enum ListSortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export interface AddNewItemButton {
  label?: string;
}

export interface IconIndicator {
  enabled: boolean;
  iconName: string;
  align?: IconIndicatorAlign;
}

export interface IconIndicatorAlign {
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'center' | 'bottom';
}

export interface ListContext extends ElementContext<'list'> {
  element: ListDefinition;
}

export interface ListResolvedContext extends ElementContext<'list'> {
  element: ListDefinition;
  workflowContext?: WorkflowContext;
  workflowsToFinalize?: AiWorkflowFinalizationEntry[];
  customTemplateCode?: string;
}

export interface ListTemplateImageOptions {
  emptyImageState?: EmptyImageState;
  imageShape?: ListImageShape;
  imageBehavior?: ImageBehavior;
  radius?: number;
  width?: number;
}

export interface HorizontalListTemplate extends ListTemplateImageOptions {
  $type: 'horizontal';
}

export interface VerticalListTemplate extends ListTemplateImageOptions {
  $type: 'vertical';
}

export interface NotificationListTemplate extends ListTemplateImageOptions {
  $type: 'notification';
}

export interface SubscriptionListTemplate extends ListTemplateImageOptions {
  $type: 'subscription';
  bulletIconName?: string;
}

export interface ECommerceListTemplate extends ListTemplateImageOptions {
  $type: 'ecommerce';
}

export interface CustomListTemplate {
  $type: 'html';
}

export type ListTemplate =
  | HorizontalListTemplate
  | VerticalListTemplate
  | NotificationListTemplate
  | SubscriptionListTemplate
  | ECommerceListTemplate
  | CustomListTemplate;

export enum ListImageShape {
  Square = 'Square',
  Circle = 'Circle',
  Landscape = 'Landscape',
  LandscapeShort = 'LandscapeShort',
  Portrait = 'Portrait',
  PortraitShort = 'PortraitShort',
}

export enum ListBehavior {
  HorizontalScrolling = 'HorizontalScrolling',
  VerticalScrolling = 'VerticalScrolling',
  Paginate = 'Paginate',
  FullList = 'FullList',
  FixedList = 'FixedList',
}

export enum ListMobileBehavior {
  Swipe = 'Swipe',
  Scroll = 'Scroll',
}

export interface ListStyles {
  padding?: PaddingStyle;
  descriptionColor?: string;
  actionsColor?: string;
  accentColor?: string;
  fill?: FillStyle;
  itemHover?: FillStyle;
  outline?: OutlineStyle;
  width?: number; // width of item's border
  radius?: number; // radius of item's border
  shadow?: ShadowStyle;
  titleColor?: string;
  titleFont?: string;
  titleFontWeight?: FontWeightStyle;
  titleItalic?: boolean;
  titleUnderline?: boolean;
  titleStrikethrough?: boolean;
  titleFontSize?: number;
  titleAlignment?: HorizontalAlignmentStyle;
}
